// DESIGN-SOURCE: none — not in Figma — SE-7324: covered-share field requested from design (Miguel); no new style values, mirrors the Duration field
/**
 * Container for the Loan action modal.
 *
 * Manages loan fee, duration and covered-share draft state. On confirm,
 * delegates to the parent callback with fee, duration and covered share.
 * Supports edit mode with a "Cancel Loan" action to remove the assignment.
 *
 * THE COVERED SHARE IS PERSISTED (SE-7324). It is the share of the wage the
 * BORROWING club covers during the loan, as a whole number 0–100, and it
 * rides `financialDetails.coveredSharePercent` on the scenario mutation. A
 * new loan needs it before Confirm enables; a loan saved before the field
 * existed (`initialCoveredSharePercent == null`) may still be updated with it
 * blank so a legacy row is never locked behind a value nobody recorded. The
 * input is digits-only and is NOT clamped: a value above 100 stays as typed
 * and surfaces an inline error, because silently rewriting what the user
 * entered hides the mistake instead of showing it.
 * @module features/squad-planning/components/LoanActionModal
 */

import { type Href, useRouter } from "expo-router";
import React, { memo, useCallback, useMemo, useState } from "react";

import { POSTHOG_FLAGS } from "@/config/featureFlags";
import usePostHogFeatureFlag from "@/hooks/usePostHogFeatureFlag";
import { buildComplianceHref } from "@/features/financial-compliance/navigation/complianceRoute";

import { useLiveImpact } from "../../../hooks/useLiveImpact";

import LoanActionModalView from "./LoanActionModalView";

import type { ActionTerms } from "@/features/financial-compliance/domain/actionFinancials";

/** Largest covered share the modal accepts, as a percentage. */
const MAX_COVERED_SHARE_PERCENT = 100;

/** Inline error shown when the typed share is out of range. */
const COVERED_SHARE_RANGE_ERROR = "Enter a whole number from 0 to 100";

/** Props for {@link LoanActionModalContainer} */
export interface LoanActionModalProps {
  /** Whether the modal is open */
  readonly isOpen: boolean;
  /** Called to close the modal */
  readonly onClose: () => void;
  /**
   * Called with loan fee, duration and covered share on confirm. The share
   * is a whole number 0–100 when one was typed, null when left blank.
   */
  readonly onConfirm: (
    loanFee: number | null,
    durationMonths: number | null,
    coveredSharePercent: number | null
  ) => void;
  /** Called to remove the action (edit mode only) */
  readonly onCancelAction?: () => void;
  /** Player name for the title */
  readonly playerName: string;
  /**
   * Player the action applies to.
   *
   * Optional so no mount point breaks by omitting it; the Live Impact verdict
   * uses it to avoid double-counting a player who is already staged.
   */
  readonly playerId?: string | null;
  /** Initial loan fee value (for edit mode) */
  readonly initialLoanFee: number | null;
  /** Initial duration in months (for edit mode) */
  readonly initialDuration: number | null;
  /**
   * Stored covered share (for edit mode). `null` for a new loan and for a
   * loan persisted before the field existed.
   */
  readonly initialCoveredSharePercent: number | null;
  /** Whether the modal is in edit mode (existing action) */
  readonly isEditMode?: boolean;
}

/**
 * Parse the covered-share text into the value `onConfirm` carries.
 * @param text - Digits-only text from the input
 * @returns The typed share as a number, or null when blank
 */
const parseCoveredShare = (text: string): number | null =>
  text === "" ? null : parseInt(text, 10);

/**
 * Container managing Loan action modal state.
 * @param root0 - Component props
 * @param root0.isOpen - Whether modal is visible
 * @param root0.onClose - Close handler
 * @param root0.onConfirm - Confirm handler with loan fee, duration and covered share
 * @param root0.onCancelAction - Remove action handler (edit mode)
 * @param root0.playerName - Player name for display
 * @param root0.playerId - Player the action applies to
 * @param root0.initialLoanFee - Initial loan fee for edit mode
 * @param root0.initialDuration - Initial duration for edit mode
 * @param root0.initialCoveredSharePercent - Stored covered share for edit mode
 * @param root0.isEditMode - Whether in edit mode
 */
const LoanActionModalContainer = ({
  isOpen,
  onClose,
  onConfirm,
  onCancelAction,
  playerName,
  playerId = null,
  initialLoanFee,
  initialDuration,
  initialCoveredSharePercent,
  isEditMode = false,
}: LoanActionModalProps) => {
  const [loanFee, setLoanFee] = useState<number | null>(initialLoanFee);
  const [durationText, setDurationText] = useState(
    initialDuration != null ? String(initialDuration) : ""
  );
  // Create mode starts EMPTY: seeding the domain default would let a loan be
  // confirmed at a share nobody chose. A stored 0 must prefill as "0".
  const [coveredShareText, setCoveredShareText] = useState(
    initialCoveredSharePercent != null ? String(initialCoveredSharePercent) : ""
  );
  const router = useRouter();

  const coveredShare = parseCoveredShare(coveredShareText);
  const coveredShareError =
    coveredShare != null && coveredShare > MAX_COVERED_SHARE_PERCENT
      ? COVERED_SHARE_RANGE_ERROR
      : null;
  // A legacy loan that never recorded a share may be updated without one;
  // every new loan, and every loan that already has a share, requires it.
  const isCoveredShareRequired = !(
    isEditMode && initialCoveredSharePercent == null
  );

  const terms = useMemo<ActionTerms>(
    () => ({
      kind: "Loan",
      loanFee,
      durationMonths: durationText !== "" ? Number(durationText) : null,
      // The typed share verbatim; null while blank, never 0, so a blank field
      // is not read as a loan the club pays nothing towards. An out-of-range
      // share is withheld too: the domain would clamp 150 to 100 and report a
      // verdict at a share nobody entered.
      wageSharePercent: coveredShareError == null ? coveredShare : null,
    }),
    [loanFee, durationText, coveredShare, coveredShareError]
  );
  const { model: liveImpact, scenarioId } = useLiveImpact(terms, playerId);
  // SE-8003: the Compliance tab is unreachable with the flag off, so this
  // link must not navigate into it. `=== true` is the fail-safe idiom — an
  // unconfigured flag, a still-loading PostHog client, or any non-`true`
  // value all skip the navigation, never perform it.
  const isComplianceEnabled =
    usePostHogFeatureFlag(POSTHOG_FLAGS.SQUAD_FINANCIALS_TAB) === true;

  const handleViewFullImpact = useCallback(() => {
    onClose();
    if (isComplianceEnabled) {
      router.push(buildComplianceHref(scenarioId) as Href);
    }
  }, [router, scenarioId, onClose, isComplianceEnabled]);

  const handleLoanFeeChange = useCallback((value: number | null) => {
    setLoanFee(value);
  }, []);

  const handleDurationChange = useCallback((text: string) => {
    setDurationText(text.replace(/\D/g, ""));
  }, []);

  const handleCoveredShareChange = useCallback((text: string) => {
    // Digits only, and NOT clamped: an out-of-range value stays visible with
    // the inline error rather than being silently rewritten.
    setCoveredShareText(text.replace(/\D/g, ""));
  }, []);

  const handleConfirm = useCallback(() => {
    const months = durationText !== "" ? parseInt(durationText, 10) : null;
    onConfirm(
      loanFee,
      Number.isNaN(months) ? null : months,
      parseCoveredShare(coveredShareText)
    );
  }, [loanFee, durationText, coveredShareText, onConfirm]);

  if (!isOpen) return null;

  return (
    <LoanActionModalView
      isOpen={isOpen}
      playerName={playerName}
      loanFee={loanFee}
      durationText={durationText}
      coveredShareText={coveredShareText}
      coveredShareError={coveredShareError}
      isCoveredShareRequired={isCoveredShareRequired}
      onLoanFeeChange={handleLoanFeeChange}
      onDurationChange={handleDurationChange}
      onCoveredShareChange={handleCoveredShareChange}
      onConfirm={handleConfirm}
      onCancel={onClose}
      onCancelAction={onCancelAction}
      isEditMode={isEditMode}
      liveImpact={liveImpact}
      onViewFullImpact={handleViewFullImpact}
    />
  );
};

LoanActionModalContainer.displayName = "LoanActionModalContainer";

export default memo(LoanActionModalContainer);
